import { Suspense, lazy, useEffect, useRef } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Home from './pages/Home'
import Login from './pages/Login'
import Register from './pages/Register'
import Contact from './pages/Contact'
import Mentions from './pages/legal/Mentions'
import Cgv from './pages/legal/Cgv'
import Privacy from './pages/legal/Privacy'
import Refund from './pages/legal/Refund'
import RequireAuth from './components/auth/RequireAuth'
import GuestOnly from './components/auth/GuestOnly'
// Bandeau cookies MASQUÉ pour l'instant, à la demande. Pour le remettre :
// décommenter l'import et la balise <CookieBanner /> plus bas. Le composant,
// ses textes (t.cookies) et son CSS (.u-cb) sont intacts et fonctionnels.
// import CookieBanner from './components/urself/CookieBanner'
import { trackPageView } from './lib/tracking'
import { pingVisit, pingCta } from './lib/notify'
import { affiliateCta } from './lib/affiliate'
// L'espace client et l'onboarding sont chargés À LA DEMANDE (React.lazy) :
// ils pèsent plus de la moitié du bundle, et un visiteur venu de Google sur
// un guide ou sur l'accueil n'en a pas besoin. Avant, chaque page publique
// téléchargeait et exécutait tout le tableau de bord — du temps de
// processeur perdu avant que la page ne réponde au premier tap (INP, une
// des trois mesures Core Web Vitals que Google classe). Ces routes ne sont
// jamais prérendues, donc l'hydratation des pages publiques n'attend rien.
const Onboarding = lazy(() => import('./pages/Onboarding'))
const DashLayout = lazy(() => import('./components/urself/DashLayout'))
const Overview = lazy(() => import('./pages/dashboard/Overview'))
const Protection = lazy(() => import('./pages/dashboard/Protection'))
const Rank = lazy(() => import('./pages/dashboard/Rank'))
const Goals = lazy(() => import('./pages/dashboard/Goals'))
const Rewards = lazy(() => import('./pages/dashboard/Recompenses'))
const Tutorial = lazy(() => import('./pages/dashboard/Tutorial'))
const Subscription = lazy(() => import('./pages/dashboard/Subscription'))
const Account = lazy(() => import('./pages/dashboard/Account'))
const Sos = lazy(() => import('./pages/dashboard/Sos'))
// Homonyme de la page publique `Contact` : celle-ci vit DANS le tableau de
// bord, d'ou l'alias — les confondre monterait la page publique dans la
// coquille du dashboard, avec deux barres de navigation superposees.
const DashContact = lazy(() => import('./pages/dashboard/Contact'))
import Guides from './pages/Guides'
import Guide from './pages/Guide'
import NotFound from './pages/NotFound'
import { PRERENDER } from './lib/prerender'

/**
 * React Router conserve la position de défilement d'une page à l'autre :
 * cliquer sur une formule en bas de la landing ouvrait l'inscription
 * déjà scrollée au milieu du formulaire.
 *
 * On ne réagit qu'au `pathname`, jamais au hash : les ancres de la landing
 * (#tarifs, #app…) doivent continuer à descendre normalement.
 */
function ScrollToTop () {
  const { pathname } = useLocation()

  useEffect(() => {
    // 'instant' et non 'auto' : global.css pose `scroll-behavior: smooth`
    // sur <html>, et 'auto' signifie « suis le CSS » — la préférence de
    // mouvement réduit aurait été ignorée.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: reduce ? 'instant' : 'smooth' })
  }, [pathname])

  return null
}

/**
 * PageView du pixel Meta à chaque changement de page.
 *
 * `index.js` en envoie un au démarrage, et c'est le SEUL que Meta verrait
 * sans ceci : dans une application à page unique, passer de `/` à `/register`
 * ne recharge rien. Meta ne saurait donc jamais que le formulaire a été
 * atteint, et on ne pourrait pas recibler les visiteurs qui l'ont vu.
 *
 * Le premier rendu est ignoré, sinon la page d'entrée compterait deux fois :
 * une par `index.js`, une par cet effet.
 */
function PixelPageViews () {
  const { pathname } = useLocation()
  const first = useRef(true)

  useEffect(() => {
    if (first.current) { first.current = false; return }
    if (PRERENDER) return
    trackPageView()
  }, [pathname])

  return null
}

/**
 * Notifications Telegram de haut de tunnel.
 *
 * Deux choses, et une seule fois pour toute l'application :
 *
 *  - LA VISITE, au montage. `pingVisit()` se dédoublonne lui-même par
 *    session, et le serveur applique en plus un délai de garde par IP :
 *    sans ça, un visiteur qui recharge la page ferait taire le bot (Telegram
 *    coupe autour de vingt messages par minute).
 *
 *  - LES CLICS VERS L'INSCRIPTION, par un écouteur unique posé sur le
 *    document. Il y a une dizaine de boutons « Commencer » répartis dans le
 *    hero, les relances, les tarifs, la barre collante et le CTA final :
 *    remonter un `onClick` dans chacun serait dix endroits à maintenir et à
 *    oublier. On lit la destination du lien cliqué, ce qui les couvre tous,
 *    y compris ceux qui n'existent pas encore.
 */
function TelegramPings () {
  const { pathname } = useLocation()

  // La page vue, à CHAQUE changement d'URL — pas seulement au premier
  // chargement. La fonction se dédoublonne par (session + chemin) et ignore
  // l'espace client. Le Chrome du prérendu n'est pas un visiteur.
  useEffect(() => { if (!PRERENDER) pingVisit() }, [pathname])

  useEffect(() => {
    if (PRERENDER) return

    function onClick (e) {
      const a = e.target.closest?.('a')
      if (!a) return
      const href = a.getAttribute('href') || ''
      if (!href.includes('/register')) return

      // Le libellé du bouton dit d'où vient le clic : c'est ce qui permet de
      // savoir quelle relance travaille et laquelle ne sert à rien.
      const txt = (a.textContent || '').trim().slice(0, 60)
      pingCta(`${txt || 'lien'} — depuis ${window.location.pathname}`)
      // Le clic « Start » compte aussi pour l'affilié qui a amené ce visiteur.
      affiliateCta()
    }

    // En phase de capture : un `preventDefault` sur le lien ne nous ferait
    // pas manquer le clic.
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  return null
}

// Le routeur central. L'espace client = routes imbriquées sous DashLayout.
export default function App () {
  return (
    <>
      <ScrollToTop />
      <PixelPageViews />
      <TelegramPings />
      {/* <CookieBanner /> */}
      {/* Pas d'écran d'attente : les morceaux chargés à la demande arrivent
          en quelques dizaines de millisecondes sur la connexion déjà
          ouverte, et le tableau de bord affiche ses propres états de
          chargement. */}
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
        <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
        <Route path="/welcome" element={<Onboarding />} />

        <Route path="/contact" element={<Contact />} />
        <Route path="/mentions-legales" element={<Mentions />} />
        <Route path="/cgv" element={<Cgv />} />
        <Route path="/confidentialite" element={<Privacy />} />
        <Route path="/remboursement" element={<Refund />} />

        {/* Les guides : le contenu de recherche (voir src/content/guides).
            Chaque guide vise une requête précise et renvoie vers /register. */}
        <Route path="/guides" element={<Guides />} />
        <Route path="/guides/:slug" element={<Guide />} />

        <Route path="/dashboard" element={<RequireAuth><DashLayout /></RequireAuth>}>
          <Route index element={<Overview />} />
          <Route path="protection" element={<Protection />} />
          <Route path="sos" element={<Sos />} />
          <Route path="tutoriel" element={<Tutorial />} />
          <Route path="rang" element={<Rank />} />
          <Route path="objectifs" element={<Goals />} />
          <Route path="recompenses" element={<Rewards />} />
          <Route path="abonnement" element={<Subscription />} />
          <Route path="compte" element={<Account />} />
          <Route path="contact" element={<DashContact />} />
          {/* anciennes URL -> nouvelle page fusionnée */}
          <Route path="enfants" element={<Navigate to="/dashboard/protection" replace />} />
          <Route path="appareils" element={<Navigate to="/dashboard/protection" replace />} />
        </Route>

        {/* Une vraie 404 (noindex) plutôt qu'une page blanche : sans elle,
            une URL fautive renvoyait la coquille vide avec un statut 200, et
            Google l'indexait comme une page du site. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  )
}
